<script lang="ts" setup>
  import { store } from '@/common/root';
  import { storeToRefs } from 'pinia';
  import useStorageClusterStore from '@/common/store/pinia/storageCluster';
  import takeFirst from '@/common/utils/takeFirst';
  import { onBeforeMount, computed } from 'vue';
  import { useI18n } from '@/common/composables/vue-i18n.composable';

  const { t } = useI18n();
  const storageClusterStore = useStorageClusterStore();

  const {
    current: storageCluster,
    statusText: storageStatusText,
    statusType: storageStatusType
  } = storeToRefs(storageClusterStore);

  const name = computed<string|undefined>(() => storageCluster.value?.name);

  const widgetColumns = computed(() => [
    {
      component: t('Storage'),
      text: storageStatusText.value || t('Unknown'),
      type: storageStatusType.value || t('Unknown')
    },
    {
      component: t('Compute'),
      text: store.getters['compute/computeClusterStatusText'] || t('Unknown'),
      type: store.getters['compute/clusterStatusType'] || t('Unknown')
    },
  ]);

  const statuses = computed(() => widgetColumns.value.map(column => ({
    component: column.component,
    status: column.type,
    text: column.text
  })).filter(item => item.status !== 'default'));

  const cumulativeStatus = computed(() => {
    const counters = statuses.value.reduce((dst, obj) => {
      if (obj.status === 'success') {
        dst.success += 1;
        return dst;
      }

      if (obj.status === 'warning') {
        dst.warning += 1;
        return dst;
      }

      dst.error += 1;
      return dst;
    }, { success: 0, warning: 0, error: 0 });

    if (counters.error > 0) return 'error';
    if (counters.warning > 0) return 'warning';
    return 'success';
  });

  onBeforeMount(async () => {
    await Promise.all([
      store.$takeFirst('compute/FETCH_COMPUTE_CLUSTER'),
      takeFirst(storageClusterStore.fetch)
    ]);
  });

  defineExpose({
    name,
    storageCluster,
    storageStatusText,
    storageStatusType,
    widgetColumns,
    statuses,
    cumulativeStatus,
    storageClusterStore
  });
</script>

<template>
  <div
    v-if="storageCluster"
    class="status-widget v-cluster-status-widget"
    data-testid="cluster-status-widget"
    :data-test-value="cumulativeStatus"
  >
    <el-popover
      ref="popover"
      popper-class="status-widget__arrow"
      :width="240"
    >
      <div class="mx-16 my-8 el-text status-widget_name el-text--body-24">
        {{ $t('Name') }}{{ ':' }}
        <strong>{{ name }}</strong>
      </div>
      <el-divider></el-divider>
      <div class="status-widget_box">
        <div
          v-for="item in statuses"
          :key="item.component"
          class="mx-16 status-widget_row el-text el-text--body-24"
          :data-testid="`${item.component}-cluster-row`"
        >
          {{ item.component }}
          <div class="status-widget_status">
            <el-icon
              v-if="item.status === 'success'"
              color="fixed-success"
              left
              name="i-check-circle--16"
            />
            <el-icon
              v-else-if="item.status === 'warning'"
              color="fixed-warning"
              left
              name="i-exclamation-circle--16"
            />
            <el-icon
              v-else
              color="fixed-danger"
              left
              name="i-times-circle--16"
            /><span class="status-widget_status__text">{{ item.text }}</span>
          </div>
        </div>
      </div>
    </el-popover>
    <el-button
      v-popover:popover
      class="status-widget_button"
      type="ghost"
    >
      <div
        class="status-widget_button-status"
      >
        <div class="el-text--ellipsis pr-8">
          {{ name }}
        </div>
        <el-icon
          v-if="cumulativeStatus === 'success'"
          color="fixed-success"
          name="i-check-circle--16"
        />
        <el-icon
          v-else-if="cumulativeStatus === 'warning'"
          color="fixed-warning"
          name="i-exclamation-circle--16"
        />
        <el-icon
          v-else
          color="fixed-danger"
          name="i-times-circle--16"
        />
      </div>
    </el-button>
  </div>
</template>

<style lang="scss" scoped>
  .status-widget {
    height: 64px;
    width: 256px;

    &_box {
      padding: 8px 0;
    }

    &_status {
      width: 104px;
      display: inline-flex;
      align-items: center;

      &__text {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    &_button {
      height: 100%;
      width: 100%;
      background-color: $av-nav-primary;
      color: $av-inversed-primary;
      border-top: solid $av-brand-light 1px;

      // Matches the UI Kit ghost button hover selector, which sets the text to
      // $av-fixed-link and hides it on the brand colored navigation panel
      &:not(:disabled):hover {
        color: $av-nav-text;
        background-color: $av-inversed-lightest;
      }
    }

    &_button-status {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 208px;
      font-weight: $av-font-weight-bold;
    }

    &_name {
      overflow-wrap: break-word;
    }

    &_row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 32px;
    }
  }
</style>
